import React, { useState, useEffect } from 'react';
import BackButton from '../Common/BackButton';
import { useProject } from '../../context/ProjectContext';
import { useTeamContext } from '../../context/TeamContext';
import { useDashboard } from '../../context/DashboardContext';
import { useI18n } from '../../i18n';
import ProjectsSidebar from './ProjectsSidebar';
import ProjectTaskList from './ProjectTaskList';
import ProjectTaskDetail from './ProjectTaskDetail';
import ProjectComposer from './ProjectComposer';
import TaskComposer from './TaskComposer';
import ProjectsOverview from './ProjectsOverview';
import DeleteProjectModal from './DeleteProjectModal';
import './ProjectsSection.css';

const ProjectsSection = () => {
    const { projects, isLoading, fetchProjects, activeProject, loadingProjectId, deleteProject } = useProject();
    const { teams, fetchTeams: fetchTeamsData, initialized } = useTeamContext();
    const { setActiveView } = useDashboard();
    const { t } = useI18n();
    const [showProjectComposer, setShowProjectComposer] = useState(false);
    const [showTaskComposer, setShowTaskComposer] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [editingTask, setEditingTask] = useState(null);
    const [composeStatus, setComposeStatus] = useState('todo');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Fetch projects and teams on mount
    useEffect(() => {
        fetchProjects();
        if (!initialized) {
            fetchTeamsData();
        }
    }, [fetchProjects, initialized, fetchTeamsData]);

    const handleCreateTeam = () => {
        setActiveView('teams');
    };

    const handleCreateProject = () => {
        setEditingProject(null);
        setShowProjectComposer(true);
    };

    const handleEditProject = (project) => {
        setEditingProject(project);
        setShowProjectComposer(true);
    };

    const handleCloseProjectComposer = () => {
        setShowProjectComposer(false);
        setEditingProject(null);
    };

    const handleCreateTask = (status = 'todo') => {
        setEditingTask(null);
        setComposeStatus(status);
        setShowTaskComposer(true);
    };

    const handleEditTask = (task) => {
        setEditingTask(task);
        setShowTaskComposer(true);
    };

    const handleCloseTaskComposer = () => {
        setShowTaskComposer(false);
        setEditingTask(null);
    };

    const handleDeleteProject = (project) => {
        setProjectToDelete(project);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!projectToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteProject(projectToDelete.id);
            setShowDeleteModal(false);
            setProjectToDelete(null);
        } catch (error) {
            // Error is handled in context with toast notification
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseDeleteModal = () => {
        if (!deleteLoading) {
            setShowDeleteModal(false);
            setProjectToDelete(null);
        }
    };


    // Show empty state if no teams and no projects
    if (!isLoading && initialized && teams.length === 0 && projects.length === 0) {
        return (
            <div className="projects-section projects-empty-state">
                <div className="projects-empty-content">
                    <div className="projects-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                    </div>
                    <h2>{t('legacy.welcome_to_projects')}</h2>
                    <p>{t('legacy.join_team_for_projects')}</p>
                    <button
                        className="btn-create-team"
                        onClick={handleCreateTeam}
                    >
                        {t('legacy.create_team')}
                    </button>
                </div>

                {showProjectComposer && (
                    <ProjectComposer
                        isOpen={showProjectComposer}
                        onClose={handleCloseProjectComposer}
                        project={editingProject}
                    />
                )}
            </div>
        );
    }

    // Show button to create project even if no projects exist but user has teams
    if (!isLoading && initialized && teams.length > 0 && projects.length === 0) {
        return (
            <div className="projects-section">
                <div className="projects-mobile-header">
                    <button className="projects-mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
                    </button>
                    <span className="projects-mobile-title">{t('legacy.projects_overview')}</span>
                </div>
                <div className="projects-layout">
                    <div className={`projects-sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>
                    <div className={`projects-sidebar-container ${isSidebarOpen ? 'open' : ''}`}>
                        <ProjectsSidebar
                            onCreateProject={handleCreateProject}
                            onEditProject={handleEditProject}
                            onDeleteProject={handleDeleteProject}
                            onClose={() => setIsSidebarOpen(false)}
                        />
                    </div>
                    <ProjectsOverview onCreateProject={handleCreateProject} />
                </div>

                {showProjectComposer && (
                    <ProjectComposer
                        isOpen={showProjectComposer}
                        onClose={handleCloseProjectComposer}
                        project={editingProject}
                    />
                )}

                {showTaskComposer && (
                    <TaskComposer
                        isOpen={showTaskComposer}
                        onClose={handleCloseTaskComposer}
                        task={editingTask}
                        projectId={activeProject?.id}
                        initialStatus={composeStatus}
                    />
                )}

                <DeleteProjectModal
                    isOpen={showDeleteModal}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    project={projectToDelete}
                    loading={deleteLoading}
                />
            </div>
        );
    }

    return (
        <div className="projects-section">
            <div className="projects-mobile-header">
                <button className="projects-mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                    <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
                </button>
                <span className="projects-mobile-title">{activeProject ? activeProject.title : t('legacy.projects_overview')}</span>
            </div>
            <div className="projects-layout">
                <div className={`projects-sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>
                <div className={`projects-sidebar-container ${isSidebarOpen ? 'open' : ''}`}>
                    <ProjectsSidebar
                        onCreateProject={handleCreateProject}
                        onEditProject={handleEditProject}
                        onDeleteProject={handleDeleteProject}
                        onClose={() => setIsSidebarOpen(false)}
                    />
                </div>
                {(activeProject || loadingProjectId) ? (
                    <ProjectTaskList
                        onCreateTask={handleCreateTask}
                        onEditTask={handleEditTask}
                        onEditProject={handleEditProject}
                        onDeleteProject={handleDeleteProject}
                    />
                ) : (
                    <ProjectsOverview onCreateProject={handleCreateProject} />
                )}
            </div>

            {showProjectComposer && (
                <ProjectComposer
                    isOpen={showProjectComposer}
                    onClose={handleCloseProjectComposer}
                    project={editingProject}
                />
            )}

            {showTaskComposer && (
                <TaskComposer
                    isOpen={showTaskComposer}
                    onClose={handleCloseTaskComposer}
                    task={editingTask}
                    projectId={activeProject?.id}
                    initialStatus={composeStatus}
                />
            )}

            <DeleteProjectModal
                isOpen={showDeleteModal}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                project={projectToDelete}
                loading={deleteLoading}
            />
        </div>
    );
};

export default ProjectsSection;
